import { create } from 'zustand';

import { compressImage } from '@/lib/compressImage';

import { ApiError } from '../services/apiClient';
import { documentApi } from '../services/documentService';

import type { DocumentCategory, DocumentItem, DocumentStatus } from '@/types/document.types';

type UploadPhase = 'compressing' | 'preparing' | 'uploading' | 'confirming';

interface DocumentState {
  // State
  applicationId: string | null;
  categories: DocumentCategory[];
  totalRequired: number;
  totalUploaded: number;
  isLoading: boolean;
  isUploading: string | null; // document type currently uploading
  uploadPhase: UploadPhase | null;
  uploadProgress: number; // 0–100
  uploadError: string | null;
  error: string | null;
  isReusing: string | null;

  // Actions
  loadChecklist: (applicationId: string) => Promise<void>;
  uploadDocument: (item: DocumentItem, file: File) => Promise<boolean>;
  reuseDocument: (item: DocumentItem) => Promise<boolean>;
  removeDocument: (documentId: string) => Promise<boolean>;
  cancelUpload: () => void;
  retryUpload: () => Promise<boolean>;
  getProgress: () => { completed: number; total: number };
  reset: () => void;
}

// Module-level variables for abort and retry
let activeXhr: XMLHttpRequest | null = null;
let lastUploadItem: DocumentItem | null = null;
let lastUploadFile: File | null = null;

function countItems(categories: DocumentCategory[], predicate: (item: DocumentItem) => boolean): number {
  return categories.reduce((total, cat) => total + cat.items.filter(predicate).length, 0);
}

function isDocumentSatisfied(item: DocumentItem): boolean {
  return item.status === 'uploaded' || item.status === 'verified';
}

/**
 * Upload a file to S3 using XMLHttpRequest for progress tracking.
 * Returns a promise that resolves when the upload completes.
 */
function uploadToS3WithProgress(uploadUrl: string, file: File, onProgress: (percent: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    activeXhr = xhr;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      activeXhr = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error('File upload failed'));
      }
    };

    xhr.onerror = () => {
      activeXhr = null;
      reject(new Error('File upload failed'));
    };

    xhr.onabort = () => {
      activeXhr = null;
      reject(new Error('Upload cancelled'));
    };

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

export const useDocumentStore = create<DocumentState>()((set, get) => ({
  applicationId: null,
  categories: [],
  totalRequired: 0,
  totalUploaded: 0,
  isLoading: false,
  isUploading: null,
  uploadPhase: null,
  uploadProgress: 0,
  uploadError: null,
  error: null,
  isReusing: null,

  loadChecklist: async (applicationId: string) => {
    set({ isLoading: true, error: null });

    try {
      if (!applicationId) {
        throw new Error('Application context is required before loading documents');
      }

      const response = await documentApi.getChecklist(applicationId);
      const data = response.data;

      const categories: DocumentCategory[] = data.categories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        description: cat.description,
        items: cat.items.map((item) => ({
          documentId: item.documentId,
          category: item.category,
          type: item.type,
          name: item.name,
          description: item.description,
          isRequired: item.isRequired,
          status: item.status,
          fileName: item.fileName,
          uploadedAt: item.uploadedAt,
          rejectionReason: item.rejectionReason,
          fromPreviousApplication: item.fromPreviousApplication,
          reusableDocumentId: item.reusableDocumentId,
          reusableFileName: item.reusableFileName,
          reusableUploadedAt: item.reusableUploadedAt,
          reusableStatus: item.reusableStatus,
        })),
      }));

      set({
        applicationId: data.applicationId,
        categories,
        totalRequired: data.totalRequired,
        totalUploaded: countItems(categories, isDocumentSatisfied),
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to load document checklist';
      set({
        applicationId: null,
        categories: [],
        totalRequired: 0,
        totalUploaded: 0,
        isLoading: false,
        error: message,
      });
    }
  },

  uploadDocument: async (item: DocumentItem, file: File) => {
    lastUploadItem = item;
    lastUploadFile = file;
    set({ isUploading: item.type, uploadPhase: 'compressing', uploadProgress: 0, uploadError: null, error: null });

    try {
      const applicationId = get().applicationId;
      if (!applicationId) {
        set({
          isUploading: null,
          uploadPhase: null,
          uploadProgress: 0,
          uploadError: 'Application context is required before uploading documents',
        });
        return false;
      }

      // Compress images before upload (skips non-images and small files)
      set({ uploadPhase: 'compressing' });
      const processedFile = await compressImage(file);

      // Client-side file size validation (10 MB default)
      const MAX_FILE_SIZE_MB = 10;
      const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
      if (processedFile.size > maxBytes) {
        set({
          isUploading: item.type,
          uploadPhase: null,
          uploadProgress: 0,
          uploadError: `File size exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB`,
        });
        return false;
      }

      // Step 1: Get presigned upload URL
      set({ uploadPhase: 'preparing' });
      const urlResponse = await documentApi.getUploadUrl({
        applicationId,
        category: item.category,
        type: item.type,
        filename: processedFile.name,
        contentType: processedFile.type,
        fileSize: processedFile.size,
      });

      const { uploadUrl, fileKey } = urlResponse.data;

      // Step 2: Upload file directly to S3 with progress tracking
      set({ uploadPhase: 'uploading' });
      await uploadToS3WithProgress(uploadUrl, processedFile, (percent) => {
        set({ uploadProgress: percent });
      });

      // Step 3: Confirm upload with backend
      set({ uploadPhase: 'confirming' });
      const confirmResponse = await documentApi.confirmUpload({
        applicationId,
        category: item.category,
        type: item.type,
        filename: processedFile.name,
        fileKey,
      });

      const confirmed = confirmResponse.data;
      const confirmedFileName = confirmed.fileName ?? confirmed.filename;

      // Update local state
      set((state: DocumentState) => {
        const categories = state.categories.map((cat: DocumentCategory) => ({
          ...cat,
          items: cat.items.map((i: DocumentItem) => {
            if (i.type !== item.type) return i;
            return {
              ...i,
              documentId: confirmed.documentId,
              status: confirmed.status,
              fileName: confirmedFileName,
              uploadedAt: confirmed.uploadedAt,
              rejectionReason: undefined,
              fromPreviousApplication: false,
              reusableDocumentId: undefined,
              reusableFileName: undefined,
              reusableUploadedAt: undefined,
              reusableStatus: undefined,
            };
          }),
        }));

        return {
          categories,
          totalUploaded: countItems(categories, isDocumentSatisfied),
          isUploading: null,
          uploadPhase: null,
          uploadProgress: 0,
          uploadError: null,
        };
      });

      return true;
    } catch (error) {
      if (error instanceof Error && error.message === 'Upload cancelled') {
        set({ isUploading: null, uploadPhase: null, uploadProgress: 0, uploadError: null });
        return false;
      }

      const message = error instanceof ApiError ? error.message : 'Upload failed. Please try again.';
      set({ uploadPhase: null, uploadProgress: 0, uploadError: message });
      return false;
    }
  },

  reuseDocument: async (item: DocumentItem) => {
    const applicationId = get().applicationId;
    if (!applicationId || !item.reusableDocumentId) {
      set({ error: 'No reusable document is available for this application' });
      return false;
    }

    set({ isReusing: item.type, error: null });
    try {
      const response = await documentApi.reuseDocument({
        applicationId,
        sourceDocumentId: item.reusableDocumentId,
        type: item.type,
      });
      const reused = response.data;
      const reusedFileName = reused.fileName ?? reused.filename;

      set((state: DocumentState) => {
        const categories = state.categories.map((cat: DocumentCategory) => ({
          ...cat,
          items: cat.items.map((i: DocumentItem) => {
            if (i.type !== item.type) return i;
            return {
              ...i,
              documentId: reused.documentId,
              status: reused.status,
              fileName: reusedFileName,
              uploadedAt: reused.uploadedAt,
              rejectionReason: undefined,
              fromPreviousApplication: false,
              reusableDocumentId: undefined,
              reusableFileName: undefined,
              reusableUploadedAt: undefined,
              reusableStatus: undefined,
            };
          }),
        }));

        return {
          categories,
          totalUploaded: countItems(categories, isDocumentSatisfied),
          isReusing: null,
        };
      });

      return true;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to reuse document';
      set({ isReusing: null, error: message });
      return false;
    }
  },

  cancelUpload: () => {
    if (activeXhr) {
      activeXhr.abort();
      activeXhr = null;
    }
    set({ isUploading: null, uploadPhase: null, uploadProgress: 0, uploadError: null });
  },

  retryUpload: () => {
    if (lastUploadItem && lastUploadFile) {
      return get().uploadDocument(lastUploadItem, lastUploadFile);
    }
    return Promise.resolve(false);
  },

  removeDocument: async (documentId: string) => {
    try {
      await documentApi.deleteDocument(documentId);

      set((state: DocumentState) => {
        const categories = state.categories.map((cat: DocumentCategory) => ({
          ...cat,
          items: cat.items.map((i: DocumentItem) => {
            if (i.documentId !== documentId) return i;
            return {
              ...i,
              documentId: undefined,
              status: 'pending' as DocumentStatus,
              fileName: undefined,
              uploadedAt: undefined,
              rejectionReason: undefined,
              fromPreviousApplication: undefined,
              reusableDocumentId: undefined,
              reusableFileName: undefined,
              reusableUploadedAt: undefined,
              reusableStatus: undefined,
            };
          }),
        }));

        return {
          categories,
          totalUploaded: countItems(categories, isDocumentSatisfied),
        };
      });
      return true;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to remove document';
      set({ error: message });
      return false;
    }
  },

  getProgress: () => {
    const { categories } = get();
    const total = countItems(categories, (i) => i.isRequired);
    const completed = countItems(categories, (i) => i.isRequired && isDocumentSatisfied(i));
    return { completed, total };
  },

  reset: () => {
    if (activeXhr) {
      activeXhr.abort();
      activeXhr = null;
    }
    lastUploadItem = null;
    lastUploadFile = null;
    set({
      applicationId: null,
      categories: [],
      totalRequired: 0,
      totalUploaded: 0,
      isLoading: false,
      isUploading: null,
      uploadPhase: null,
      uploadProgress: 0,
      uploadError: null,
      error: null,
      isReusing: null,
    });
  },
}));
